import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Fingerprint, FileSignature, ShieldCheck, FileText, Stamp, ArrowRight, CheckCircle2 } from "lucide-react";
import { MarketingLayout, StartServiceButton } from "@/components/marketing/MarketingLayout";
import { CtaBanner, SectionHeading } from "@/components/marketing/blocks";
import { PricingSection } from "@/components/marketing/PricingSection";
import { useSeo } from "@/hooks/useSeo";

const LOGO = "/arps-logo-hero.png";

const STATS = [
  { icon: MapPin, label: "Memphis Metro & Shelby County", sub: "Local, focused coverage" },
  { icon: Fingerprint, label: "GPS-Verified Attempts", sub: "Location & time logged" },
  { icon: FileSignature, label: "Court-Ready Affidavits", sub: "Detailed proof of service" },
  { icon: ShieldCheck, label: "Documented Chain of Custody", sub: "Every step recorded" },
];

const SERVICES = [
  {
    icon: FileText,
    title: "Process Serving",
    to: "/process-serving",
    desc: "Reliable service of summons, subpoenas, complaints, and legal notices with GPS-timestamped attempts and detailed affidavits of service.",
    points: ["Standard, rush & 24-Hour Priority options", "Photo & GPS documentation", "Affidavit of service on completion"],
  },
  {
    icon: Stamp,
    title: "Notary Services",
    to: "/notary-services",
    desc: "Mobile and on-demand notarization for real estate, legal, and business documents across the Memphis metro area and Shelby County.",
    points: ["Mobile & on-site signings", "Real estate & loan documents", "Affidavits & authorizations"],
  },
];

const COMMUNITIES = ["Memphis", "Bartlett", "Germantown", "Collierville", "Cordova", "Millington", "Arlington", "Lakeland"];

export default function Home() {
  useSeo({
    title: "AR Process Service and Notary | Memphis Process Server & Mobile Notary",
    description:
      "Professional process serving and mobile notary services in Memphis and Shelby County, Tennessee.",
    canonicalPath: "/",
  });

  return (
    <MarketingLayout>
      <section className="bg-slate-50 px-5 pb-14 pt-4 text-center sm:px-8 lg:pb-20 lg:pt-6">
        <div className="mx-auto max-w-4xl">
          <img
            data-testid="home-hero-logo"
            src={LOGO}
            alt="AR Process Service and Notary"
            className="mx-auto mb-2 block h-[12.5rem] w-auto max-w-full drop-shadow-sm sm:h-[17.5rem] sm:mb-3 lg:h-[22.5rem]"
          />
          <h1 className="sr-only">AR Process Service and Notary — Process Serving &amp; Notary Services in Memphis, TN</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl">
            Serving law firms, businesses, and individuals across Memphis and Shelby County — with GPS-verified attempts,
            photographic evidence, and court-ready affidavits on every job.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <StartServiceButton testid="start-service-cta-hero" />
            <Link
              to="/contact"
              data-testid="hero-contact-cta"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 text-base font-semibold text-slate-800 transition hover:bg-slate-100"
            >
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-10 sm:px-8">
        <div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center sm:text-left">
              <s.icon className="mx-auto h-7 w-7 text-sky-700 sm:mx-0" />
              <p className="mt-3 font-display text-base font-bold text-slate-900">{s.label}</p>
              <p className="mt-1 text-sm text-slate-500">{s.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading
          overline="What we do"
          title="Two core services, one standard of proof"
          subtitle="Whether you need documents served or notarized, every job is handled with careful documentation from start to finish."
        />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 lg:grid-cols-2">
          {SERVICES.map((sv) => (
            <div key={sv.title} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50">
                <sv.icon className="h-6 w-6 text-sky-700" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900">{sv.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600 sm:text-base">{sv.desc}</p>
              <ul className="mt-4 space-y-2">
                {sv.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[15px] text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" /> {p}
                  </li>
                ))}
              </ul>
              <Link
                to={sv.to}
                data-testid={`home-service-link-${sv.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-sky-700 hover:text-sky-800"
              >
                Learn more <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading
              overline="Coverage"
              title="Serving Memphis Metro &amp; Shelby County"
              subtitle="We focus on the greater Memphis metropolitan area and Shelby County, Tennessee — delivering careful, well-documented service on every job."
            />
            <Link
              to="/service-areas"
              className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-sky-700 hover:text-sky-800"
            >
              View service areas <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {COMMUNITIES.map((r) => (
              <div key={r} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[15px] font-medium text-slate-800">
                <MapPin className="h-5 w-5 shrink-0 text-sky-600" /> {r}
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection />

      <CtaBanner />
    </MarketingLayout>
  );
}
